// Extracted from ProductsPage.js. Laminate code
// selector dialog — search codes, pick size available for that code,
// set sheet quantity.
import React, { useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Plus, Search } from 'lucide-react';
import { formatINR } from '../../../lib/store';

// Get available sizes for the currently selected laminate code
export function getAvailableSizesForCode(code, group) {
  // If code has specific sizes, use those
  if (code?.sizes && code.sizes.length > 0) {
    return code.sizes;
  }
  // Otherwise fall back to collection-level sizes
  if (group?.available_sizes && group.available_sizes.length > 0) {
    return group.available_sizes;
  }
  // Default standard laminate sizes including 9x4
  return ['10x4', '9x4', '8x4'];
}

export function LaminateCodeSelectorDialog({
  group,
  onClose,
  codeSearch,
  setCodeSearch,
  selectedCode,
  onSelectCode,
  selectedSize,
  setSelectedSize,
  quantity,
  setQuantity,
  quantityInput,
  setQuantityInput,
  onAdd,
}) {
  // Filter laminate codes by search term
  const filteredCodes = useMemo(() => {
    if (!group?.codes_and_rates) return [];
    if (!codeSearch.trim()) return group.codes_and_rates;
    const searchLower = codeSearch.toLowerCase().trim();
    return group.codes_and_rates.filter(cr =>
      cr.code.toLowerCase().includes(searchLower)
    );
  }, [group, codeSearch]);

  return (
    <Dialog open={!!group} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="laminate-code-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#1A1C16]">Select Code & Add</DialogTitle>
        </DialogHeader>

        {group && (
          <div className="space-y-4">
            {/* Product Info */}
            <div className="p-3 bg-[#F5F6F0] rounded-lg">
              <p className="font-bold text-[#1A1C16]">{group.brand}</p>
              <p className="text-sm text-[#5C6150]">{group.name}</p>
              {group.thickness && (
                <span className="inline-block mt-2 text-xs bg-[#DAA520]/20 text-[#8B6914] px-2 py-1 rounded font-medium border border-[#DAA520]/30">
                  {group.thickness}
                </span>
              )}
            </div>

            {/* Code Search */}
            <div>
              <Label className="text-xs uppercase tracking-wide text-[#5C6150]">Search Codes</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8F9485]" />
                <Input
                  value={codeSearch}
                  onChange={(e) => setCodeSearch(e.target.value)}
                  placeholder="Type code to filter..."
                  className="pl-9 bg-[#F9FAF7] border-[#E2E6D9]"
                  data-testid="laminate-code-search"
                />
              </div>
            </div>

            {/* Code Selector */}
            <div>
              <Label className="text-xs uppercase tracking-wide text-[#5C6150]">
                Select Code {filteredCodes.length !== group.codes_and_rates.length &&
                  <span className="text-[#8F9485]">({filteredCodes.length} of {group.codes_and_rates.length})</span>
                }
              </Label>
              <div className="mt-2 max-h-48 overflow-y-auto space-y-1">
                {filteredCodes.length === 0 ? (
                  <p className="text-sm text-[#8F9485] py-2 text-center">No codes match &quot;{codeSearch}&quot;</p>
                ) : (
                  filteredCodes.map((cr, i) => (
                    <button
                      key={i}
                      onClick={() => onSelectCode(cr)}
                      className={`w-full flex justify-between items-center p-3 rounded-lg border transition-all ${
                        selectedCode?.code === cr.code
                          ? 'border-[#556B2F] bg-[#556B2F]/10'
                          : 'border-[#E2E6D9] hover:border-[#556B2F]/50'
                      }`}
                      data-testid={`laminate-code-${cr.code}`}
                    >
                      <div className="text-left">
                        <span className="font-mono text-sm font-medium">{cr.code}</span>
                        {cr.sizes && cr.sizes.length > 0 && (
                          <span className="text-xs text-[#8F9485] ml-2">({cr.sizes.join(', ')})</span>
                        )}
                      </div>
                      <span className="font-mono-data font-semibold text-[#556B2F]">{formatINR(parseFloat(cr.rate) > 0 ? cr.rate : (group.base_rate || group.min_rate || 0))}</span>
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Size Selector - Shows only sizes available for selected code */}
            {selectedCode && (
              <div>
                <Label className="text-xs uppercase tracking-wide text-[#5C6150]">Select Size</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {getAvailableSizesForCode(selectedCode, group).map((size, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedSize(size)}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                        selectedSize === size
                          ? 'border-[#556B2F] bg-[#556B2F] text-white'
                          : 'border-[#E2E6D9] hover:border-[#556B2F]/50'
                      }`}
                      data-testid={`laminate-size-${size}`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div>
              <Label className="text-xs uppercase tracking-wide text-[#5C6150]">Quantity (Sheets)</Label>
              <div className="flex items-center gap-2 mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newQty = Math.max(1, quantity - 1);
                    setQuantity(newQty);
                    setQuantityInput(String(newQty));
                  }}
                  className="border-[#E2E6D9] h-10 w-10"
                >
                  -
                </Button>
                <Input
                  type="number"
                  min="1"
                  value={quantityInput}
                  onChange={(e) => {
                    setQuantityInput(e.target.value);
                    const val = parseInt(e.target.value);
                    if (!isNaN(val) && val > 0) setQuantity(val);
                  }}
                  className="w-20 text-center font-mono-data text-lg bg-[#F9FAF7] border-[#E2E6D9] h-10"
                  data-testid="laminate-quantity-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const newQty = quantity + 1;
                    setQuantity(newQty);
                    setQuantityInput(String(newQty));
                  }}
                  className="border-[#E2E6D9] h-10 w-10"
                >
                  +
                </Button>
              </div>
            </div>

            {/* Total Preview */}
            {selectedCode && (
              <div className="p-3 bg-[#FDF6E1] rounded-lg border border-[#DAA520]/30">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-xs text-[#8B6914] uppercase tracking-wide">Selected</p>
                    <p className="font-mono text-sm font-medium text-[#1A1C16]">
                      {selectedCode.code} • {selectedSize || '8x4'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-[#8B6914] uppercase tracking-wide">Total</p>
                    <p className="text-xl font-bold font-mono-data text-[#1A1C16]">
                      {formatINR(selectedCode.rate * quantity)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Add Button */}
            <Button
              onClick={onAdd}
              disabled={!selectedCode}
              className="w-full bg-[#556B2F] hover:bg-[#465926] text-white uppercase tracking-wider font-semibold h-12 disabled:opacity-50"
              data-testid="confirm-laminate-add-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add to Quotation
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
